import {ChangeDetectionStrategy, Component, computed, ElementRef, inject, input} from '@angular/core';
import {CommonModule, DOCUMENT} from '@angular/common';
import {DonutChartComponent, NgxColor} from '../donut--chart-card/donut-chart.component';
import {Risk, RISK_COLOR, RISK_ORDER} from '@/app/utils/risk-colors';
import {getContrastingTextColor} from "@/app/utils/color.utils";

export type SimpleDatum = { name: string; value: number };

@Component({
  selector: 'app-multi-donut-card',
  standalone: true,
  imports: [CommonModule, DonutChartComponent],
  templateUrl: './multi-donut-card.component.html',
  styleUrls: ['./multi-donut-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiDonutCardComponent {
  readonly legendItems = computed(() => {
    const data = this.donutData();
    const colors = this.donutColors();
    return data.map((d, i) => ({
      color: (colors[i]?.value ?? '#000000'),
      label: d.name,
    }));
  });
  private el = inject(ElementRef);
  private document = inject(DOCUMENT);
  title = input<string>('');
  series = input<SimpleDatum[]>([]);
  totalHint = input<string | undefined>(undefined);
  maxSlices = input<number>(4);
  showLegend = input<boolean>(true);
  arcWidth = input<number>(0.4);
  view = input<[number, number]>([220, 220]);
  minLabelPct = input<number>(1);
  colors = input<NgxColor[] | undefined>(undefined);

  readonly cx = computed(() => this.view()[0] / 2);
  readonly cy = computed(() => this.view()[1] / 2);
  readonly rOuter = computed(() => Math.min(this.view()[0], this.view()[1]) / 2 - 17);

  private defaultPalette = computed(() => {
    const styles = getComputedStyle(this.document.documentElement);
    return [
      styles.getPropertyValue('--colorWomen') || '#FFAACE',
      styles.getPropertyValue('--colorMen') || '#B2DCEE'
    ];
  });

  private isRiskDataset = (items: SimpleDatum[]) =>
    items.length > 0 && items.some(d => RISK_ORDER.includes(d.name as Risk));

  private normalized = computed<SimpleDatum[]>(() => {
    const rows = Array.isArray(this.series()) ? this.series() : [];
    const mapped = rows.map(r => ({name: this.normalizeLabel(r.name), value: Number(r.value || 0)}));

    if (this.colors() && this.colors()!.length > 0) {
      return mapped;
    }

    const sinDato = mapped.filter(d => d.name === 'Sin dato').reduce((a, b) => a + b.value, 0);
    const withoutSinDato = mapped.filter(d => d.name !== 'Sin dato');

    if (this.isRiskDataset(withoutSinDato)) {
      if (sinDato > 0) withoutSinDato.push({name: 'Sin dato', value: sinDato});
      return withoutSinDato;
    }

    const isGenderChart = withoutSinDato.some(it => it.name === 'Femenino' || it.name === 'Masculino');
    if (isGenderChart) {
      const sorted = withoutSinDato.sort((a, b) => a.name.localeCompare(b.name));
      if (sinDato > 0) sorted.push({name: 'Sin dato', value: sinDato});
      return sorted;
    }

    const sorted = withoutSinDato.sort((a, b) => b.value - a.value);
    if (sinDato > 0) sorted.push({name: 'Sin dato', value: sinDato});
    return sorted;
  });

  private sliced = computed<SimpleDatum[]>(() => {
    const items = [...this.normalized()];

    if (this.colors() && this.colors()!.length > 0) {
      return items;
    }

    if (this.isRiskDataset(items)) {
      return items;
    }

    const max = this.maxSlices();
    if (items.length <= max) {
      return items;
    }

    const lastIsSinDato = items[items.length - 1]?.name === 'Sin dato';
    const tailReserve = lastIsSinDato ? [items.pop()!] : [];
    const headCount = max - (tailReserve.length ? 1 : 0);
    const head = items.slice(0, headCount);
    const rest = items.slice(headCount);
    const othersSum = rest.reduce((acc, d) => acc + d.value, 0);

    const out: SimpleDatum[] = [...head];
    if (othersSum > 0) out.push({name: 'Otros', value: othersSum});
    if (tailReserve.length) out.push(tailReserve[0]);
    return out;
  });

  readonly total = computed<number>(() =>
    this.sliced().reduce((acc, d) => acc + d.value, 0)
  );

  readonly donutData = computed(() =>
    this.sliced().map(d => ({name: d.name, value: d.value}))
  );

  readonly donutColors = computed<NgxColor[]>(() => {
    const customColors = this.colors();
    if (customColors) return customColors;

    const items = this.sliced();

    if (this.isRiskDataset(items)) {
      return items.map(d => ({
        name: d.name,
        value: RISK_COLOR[d.name as Risk] ?? '#999999'
      }));
    }

    return items.map((d, i) => ({
      name: d.name,
      value: this.defaultPalette()[i % this.defaultPalette().length]
    }));
  });

  private normalizeLabel(name: unknown): string {
    if (name === null || name === undefined) return 'Sin dato';
    const s = String(name).trim();
    return s === '—' || s === '' ? 'Sin dato' : s;
  }

  private pctExact = (value: number) => this.total() ? (value / this.total()) * 100 : 0;
  readonly overlayLabels = computed(() => {
    const data = this.donutData();
    const total = this.total();
    if (!total || data.length === 0) return [] as Array<{ text: string; x: number; y: number; fill: string }>;

    let cursorPct = 0;
    const labels: Array<{ text: string; x: number; y: number; fill: string }> = [];
    const colors = this.donutColors();
    const rLbl = this.labelRadius();

    data.forEach((d, i) => {
      const pctF = this.pctExact(d.value);
      const pctR = Math.round(pctF);
      const mid = this.midAngle(cursorPct, pctF);
      cursorPct += pctF;

      if (pctR >= this.minLabelPct()) {
        const {x, y} = this.polarToXY(mid, rLbl);
        const colorValue = colors[i]?.value ?? '#000000';
        const fill = getContrastingTextColor(colorValue, this.el.nativeElement);
        labels.push({text: `${pctR}%`, x, y, fill});
      }
    });

    return labels;
  });

  private midAngle(startPct: number, sweepPct: number): number {
    const start = (startPct / 100) * 360 - 90;
    const sweep = (sweepPct / 100) * 360;
    return start + sweep / 2;
  }

  private labelRadius(): number {
    const r = this.rOuter();
    return r - (r * this.arcWidth()) / 2;
  }

  private polarToXY(angleDeg: number, radius?: number) {
    const ang = angleDeg * (Math.PI / 180);
    const rr = radius ?? this.labelRadius();
    return {x: this.cx() + rr * Math.cos(ang), y: this.cy() + rr * Math.sin(ang)};
  }

  readonly viewWidth  = computed(() => this.view()?.[0] ?? 200);
  readonly viewHeight = computed(() => this.view()?.[1] ?? 200);
  readonly viewTuple  = computed<[number, number]>(() => [this.viewWidth(), this.viewHeight()]);

  trackByLegend = (_: number, it: { label: string; color: string }) => `${it.label}:${it.color}`;
}
